import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextInputSection from '../WidgetEditComponents/TextInputSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import SpiriFeaturesContent from '../WidgetEditComponents/SpiriFeaturesContent';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SpiritualFeatureBannerEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION (KRITISCH!)
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // Upload States
    const [isUploadingCenterImage, setIsUploadingCenterImage] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION LOGIC - KRITISCH für Template-Defaults
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SpiritualFeatureBanner initialized - layout variations now active');
            
            // ✅ TEMPLATE-DEFAULTS KORRIGIEREN
            if (editData.showCenterImage === undefined) {
                handleInputChange({ target: { name: 'showCenterImage', value: true, type: 'checkbox', checked: true }});
            }
            
            if (!editData.features || editData.features.length === 0) {
                // Standard Features erstellen falls nicht vorhanden mit neuen Properties
                const defaultFeatures = [
                    { 
                        id: 'feature1', 
                        position: 'left', 
                        title: 'Growth & self discovery', 
                        description: 'Yoga is an enriching opportunity for personal growth and self-discovery.',
                        miniTitle: 'SPIRITUAL',
                        priceLabel: '$49/month',
                        showMiniTitle: true,
                        showPriceLabel: true
                    },
                    { 
                        id: 'feature2', 
                        position: 'left', 
                        title: 'All levels welcome', 
                        description: 'We offer a diverse array of classes tailored to all skill levels.',
                        miniTitle: 'INCLUSIVE',
                        priceLabel: '$29/class',
                        showMiniTitle: true,
                        showPriceLabel: true
                    },
                    { 
                        id: 'feature3', 
                        position: 'right', 
                        title: 'Breath & stillness', 
                        description: 'We incorporate breathwork and meditation to help individuals connect deeply.',
                        miniTitle: 'MINDFUL',
                        priceLabel: '$39/session',
                        showMiniTitle: true,
                        showPriceLabel: true
                    },
                    { 
                        id: 'feature4', 
                        position: 'right', 
                        title: 'Beyond boundaries', 
                        description: 'We provide inclusive and holistic yoga lessons for overall wellbeing.',
                        miniTitle: 'HOLISTIC',
                        priceLabel: '$59/package',
                        showMiniTitle: true,
                        showPriceLabel: true
                    }
                ];
                handleInputChange({ target: { name: 'features', value: defaultFeatures }});
            }
            
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        console.log(`🚀 SpiritualFeatureBanner useEffect triggered for layout: ${currentLayout}`);
        
        // ✅ RACE CONDITION PREVENTION
        if (!isInitialized) {
            console.log('🚫 SpiritualFeatureBanner: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 SpiritualFeatureBanner: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 SpiritualFeatureBanner REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-center': {
                height: 'auto',                  // ✅ Auto Height für natürliche Proportionen
                heightMobile: 'auto',            // ✅ Auto Height Mobile
                paddingTop: 80,                  // Großzügiger Desktop Padding
                paddingTopMobile: 40,            // Kompakter Mobile Padding
                paddingBottom: 80,
                paddingBottomMobile: 40,
                featureSpacing: 40,              // Spacing zwischen Features und Center Image
                featureVerticalSpacing: 60,      // ✅ Gap zwischen Features
                contentMaxWidth: 1200,           // Max Width des Content Containers
                centerImageSize: 400,            // Center Image Size
                sideSpacing: 60,                 // Side Spacing
                showCenterImage: true,           // Center Image anzeigen
                layoutStyle: 'classic-center'    // Legacy Support
            },
            'image-top-vertical': {
                height: 'auto',                  // ✅ Auto Height für natürliche Proportionen
                heightMobile: 'auto',            // ✅ Auto Height Mobile
                paddingTop: 60,                  // Weniger Top Padding
                paddingTopMobile: 30,
                paddingBottom: 80,
                paddingBottomMobile: 40,
                featureSpacing: 30,              // Kompakteres Spacing
                featureVerticalSpacing: 40,      // ✅ Kompakterer Gap für vertikales Layout
                contentMaxWidth: 1000,           // Schmalerer Container
                centerImageSize: 350,            // Kleineres Image für vertikales Layout
                sideSpacing: 40,                 // Weniger Side Spacing
                showCenterImage: true,
                layoutStyle: 'image-top-vertical'
            },
            'compact-features': {
                height: 'auto',                  // ✅ Auto Height für natürliche Proportionen
                heightMobile: 'auto',            // ✅ Auto Height Mobile
                paddingTop: 60,                  // Kompakter Padding
                paddingTopMobile: 30,
                paddingBottom: 60,
                paddingBottomMobile: 30,
                featureSpacing: 30,              // Kompaktes Spacing
                featureVerticalSpacing: 40,      // ✅ Kompakter Gap
                contentMaxWidth: 1100,
                centerImageSize: 320,            // Kleineres Image
                sideSpacing: 50,
                showCenterImage: true,
                layoutStyle: 'classic-center'
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const updatedEditData = { ...editData, ...variations };
            
            console.log(`🔧 Batch updating editData:`, {
                before: { height: editData.height, heightMobile: editData.heightMobile },
                after: { height: updatedEditData.height, heightMobile: updatedEditData.heightMobile }
            });
            
            // ✅ DIREKTE onUpdate statt einzelne handleInputChange calls
            onUpdate(updatedEditData);
            
            console.log(`✅ Layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate]);

    // 🎯 LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 SpiritualFeatureBanner Layout changed to: ${layoutId}`);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Feature Management (4 fixed features)
    const updateFeature = (index, field, value) => {
        const currentFeatures = editData.features || [];
        const updatedFeatures = [...currentFeatures];
        if (updatedFeatures[index]) {
            updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
            handleInputChange({ target: { name: 'features', value: updatedFeatures } });
        }
    };

    // Color Configuration - Mini Titles und Price Labels übernehmen bestehende Farben
    const colorConfig = [
        [
            { fieldName: 'featureTitleColor', label: 'Feature Titles', defaultValue: '#2c3e50', icon: '/img/text-fat.svg' },
            { fieldName: 'featureDescriptionColor', label: 'Feature Descriptions', defaultValue: '#5a5a5a', icon: '/img/text.svg' },
            { fieldName: 'featureTitleSeparatorColor', label: 'Title Separator', defaultValue: '#5C80BC', icon: '/img/line.svg' },
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#f9f8f6', icon: '/img/background.svg' }
        ]
    ];

    // Font Configuration - Behalte Font-Einstellungen für alle Text-Elemente
    const fontConfig = [
        { label: 'Feature Titles', fieldName: 'featureTitleFont', fontType: 'big' },
        { label: 'Feature Descriptions', fieldName: 'featureDescriptionFont', fontType: 'small' },
        { label: 'Mini Titles', fieldName: 'featureMiniTitleFont', fontType: 'small' },
        { label: 'Price Labels', fieldName: 'featurePriceLabelFont', fontType: 'small' }
    ];

    // 🎯 CUSTOM LAYOUT PROPERTIES - Feature-spezifische Properties
    const customLayoutProperties = [
        {
            section: 'Feature Settings',
            icon: 'fa-star',
            properties: [
                {
                    id: 'featureSpacing',
                    label: 'Feature Spacing',
                    min: 20,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '🎆',
                    description: 'Space between features and center image',
                    defaultValue: 40
                },
                {
                    id: 'featureVerticalSpacing',
                    label: 'Feature Gap',
                    min: 20,
                    max: 120,
                    step: 10,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Vertical gap between features',
                    defaultValue: 60
                },
                {
                    id: 'contentMaxWidth',
                    label: 'Content Max Width',
                    min: 800,
                    max: 1400,
                    step: 50,
                    unit: 'px',
                    icon: '📏',
                    description: 'Maximum width of the content container',
                    defaultValue: 1200
                }
            ]
        },
        {
            section: 'Image Settings',
            icon: 'fa-image',
            properties: [
                {
                    id: 'centerImageSize',
                    label: 'Center Image Size',
                    min: 200,
                    max: 500,
                    step: 20,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Size of the center feature image',
                    defaultValue: 400
                },
                {
                    id: 'sideSpacing',
                    label: 'Side Spacing',
                    min: 20,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Left and right spacing of the content',
                    defaultValue: 60
                }
            ]
        }
    ];

    // ✅ ADVANCED SETTINGS - Title Separator Configuration (nach HeroHeader Pattern)
    const advancedConfig = [
        {
            fieldName: 'showFeatureTitleSeparator',
            label: 'Feature Title Separator',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/line.svg',
            description: 'Add decorative separator under feature titles'
        },
        // ✅ SEPARATOR SETTINGS - nur sichtbar wenn Toggle aktiv
        ...(editData.showFeatureTitleSeparator ? [
            {
                fieldName: 'featureTitleSeparatorThickness',
                label: 'Separator Thickness',
                type: 'slider',
                defaultValue: 2,
                min: 1,
                max: 6,
                step: 1,
                unit: 'px',
                icon: '/img/height.svg',
                description: 'Thickness of the separator line'
            },
            {
                fieldName: 'featureTitleSeparatorWidth',
                label: 'Separator Width',
                type: 'slider',
                defaultValue: 30,
                min: 10,
                max: 100,
                step: 5,
                unit: '%',
                icon: '/img/width.svg',
                description: 'Width of the separator element (% of feature)'
            },
            {
                fieldName: 'featureTitleSeparatorSpacing',
                label: 'Separator Spacing',
                type: 'slider',
                defaultValue: 8,
                min: 4,
                max: 20,
                step: 2,
                unit: 'px',
                icon: '/img/spacing.svg',
                description: 'Space between title and separator'
            }
        ] : [])
    ];

    return (
        <div className="widget-edit-container">
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR */}
                <LayoutSelector
                    widgetType="spiritualFeatureBanner"
                    selectedLayout={editData.layoutType || 'classic-center'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* ✅ IMAGE CONTAINER */}
                <ImageContainer
                    label="Center Image"
                    fieldName="centerImage"
                    currentImage={editData.centerImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingCenterImage}
                    setIsUploading={setIsUploadingCenterImage}
                    showToggle={true}
                    toggleLabel="Show Center Image"
                    toggleFieldName="showCenterImage"
                    toggleChecked={editData.showCenterImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Image Size',
                            fieldName: 'centerImageSize',
                            defaultValue: 400,
                            min: 200,
                            max: 500,
                            step: 20,
                            unit: 'px'
                        },
                        {
                            type: 'slider',
                            label: 'Border Radius',
                            fieldName: 'centerImageBorderRadius',
                            defaultValue: 12,
                            min: 0,
                            max: 50,
                            step: 2,
                            unit: 'px'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />
                
                {/* ✅ SPIRI FEATURES CONTENT */}
                <SpiriFeaturesContent 
                    features={editData.features || []}
                    updateFeature={updateFeature}
                    maxFeatures={4}
                    featuresLabel="Spiritual Features"
                    showPositionControl={false}
                    editData={editData}
                    handleInputChange={handleInputChange}
                />
                
                {/* ✅ ANIMATION SETTINGS */}
                <AnimationSettingsSection 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    animationConfig={{
                        enableField: 'enableAnimations',
                        typeField: 'animationType',
                        durationField: 'animationDuration',
                        delayField: 'animationDelay'
                    }}
                />
                
                {/* ✅ REVOLUTIONARY LAYOUT SETTINGS */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />
                
                {/* ✅ ADVANCED SETTINGS - Title Separator */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />
                
                {/* ✅ MORE SETTINGS */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - Layout handled by RevolutionaryLayoutSettings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default SpiritualFeatureBannerEdit;
